import * as PIXI from 'pixi.js';
import { IBouncer } from 'js/bubbleCore/types';
import { IHexPixelSize } from 'js/bubbleCore/view/types';
import { spacePositionToPxPosition } from 'js/bubbleCore/view/utils/spacePositionToPxPosition';
import { Bubble } from 'js/bubbleCore/bubbles/types';
import { Lerper } from 'framework/utils/Lerper';
import { Range } from 'framework/utils/Range';
import { clamp } from 'framework/utils/math-utils';

export class BouncerView extends PIXI.Container {
  private readonly alphaLerper: Lerper;
  private readonly model: IBouncer;
  private gfx: PIXI.Graphics;
  private readonly alphaSizeOffset: number;

  constructor(
    model: IBouncer,
    pixelSize: IHexPixelSize,
    alphaSizeOffset: number = 0,
  ) {
    super();
    this.model = model;
    this.alphaSizeOffset = alphaSizeOffset;
    this.gfx = this.addChild(new PIXI.Graphics());
    this.initView(model, pixelSize);

    this.alphaLerper = new Lerper({
      initValue: 0.2,
      lerp: 0.3,
      autoUpdate: true,
      snap: 0.001,
      range: new Range(0.2, 1),
      destroySignal: this.onDestroyed,
    });
    this.alphaLerper.signals.onValueUpdate.add((value) => this.alpha = value);
    this.alphaLerper.signals.onUpdate.add(() => {
      this.alphaLerper.setTarget(this.alphaLerper.getTarget() - 0.02);
    });
  }

  public handleBubbleUpdate(bubble: Bubble): void {
    const activationDistance = 8;
    const rect = this.model.size;

    if (
      bubble.hexSpaceX > (rect.x - this.alphaSizeOffset) &&
      bubble.hexSpaceX < (rect.x + rect.width + this.alphaSizeOffset) &&
      bubble.hexSpaceY > rect.y - activationDistance
    ) {
      const alphaTarget = clamp(
        1 - ((rect.y - bubble.hexSpaceY) / activationDistance), 0, 1,
      );
      if (alphaTarget > this.alphaLerper.getTarget()) {
        this.alphaLerper.setTarget(alphaTarget);
      }
    }
  }

  private initView(model: IBouncer, pixelSize: IHexPixelSize): void {
    this.gfx.clear();
    this.gfx.beginFill(0xffffff, 1);

    const {x: right, y: bottom} = spacePositionToPxPosition(
      model.size.x + model.size.width,
      model.size.y + model.size.height,
      pixelSize,
    );

    const {x: left, y: top } = spacePositionToPxPosition(
      model.size.x,
      model.size.y,
      pixelSize,
    );

    const pxWidth = right - left;
    const pxHeight = bottom - top;

    this.gfx.drawRect(0, 0, pxWidth, pxHeight);

    spacePositionToPxPosition(
      model.size.x,
      model.size.y,
      pixelSize,
      this.position as PIXI.Point
    );
  }
}